import { z } from 'zod';

import { sanitize } from '@intake24/common/rules';

import { actionItem } from '../prompts/action-item.ts';

export const databaseTypes = ['foods', 'system'] as const;
export type DatabaseType = typeof databaseTypes[number];

export const environmentOptions = ['development', 'test', 'production'] as const;
export type Environment = (typeof environmentOptions)[number];

export const frontEnds = ['admin', 'survey'] as const;
export type FrontEnd = (typeof frontEnds)[number];

export const applications = [...frontEnds, 'api', 'shared'] as const;
export type Application = (typeof applications)[number];

export const isApplication = (app: any): app is Application => applications.includes(app);

export const customField = z.object({
  name: z.string().min(1).max(128),
  value: z.union([z.string(), z.number(), z.boolean(), z.date()]).pipe(z.coerce.string<string>().min(1).max(512)),
});
export type CustomField = z.infer<typeof customField>;

export const userCustomField = customField.extend({
  public: z.boolean().optional(),
});
export type UserCustomField = z.infer<typeof userCustomField>;

export const customFieldRecord = z.record(customField.shape.name, customField.shape.value);
export type CustomFieldRecord = z.infer<typeof customFieldRecord>;

export type Dictionary<T = any> = { [key: string]: T };

export type Nullable<T> = {
  [P in keyof T]: T[P] | null;
};

export type PartialRecord<K extends keyof any, T> = {
  [P in K]?: T;
};

export type Optional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

export type OmitAndOptional<T, KOmit extends keyof T, KOptional extends keyof T> = Omit<
  T,
  KOmit | KOptional
>
& Partial<Pick<T, KOptional>>;

export type WithKey<K extends string | number | symbol> = {
  [k in K]: string;
};

export type UnwrapAII<P> = P extends AsyncIterableIterator<infer T> ? T : never;

export const emailCopy = ['cc', 'bcc', 'none'] as const;
export type EmailCopy = (typeof emailCopy)[number];

export const localeTranslation = z.record(z.string(), z.string().nullable());
export const sanitizedLocaleTranslation = localeTranslation.transform(val =>
  sanitize(val, { allowHtml: true, emptyStringToNull: true }));
export type LocaleTranslation = z.infer<typeof localeTranslation>;

export const localeTranslationStrict = z.record(z.string(), z.string());
export type LocaleTranslationStrict = z.infer<typeof localeTranslationStrict>;

export const localeTranslations = z.record(z.string(), z.string().array());
export type LocaleTranslations = z.infer<typeof localeTranslations>;

export const requiredLocaleTranslation = z.intersection(
  z.object({ en: z.string() }),
  z.record(z.string(), z.string().nullable()),
);
export const sanitizedRequiredLocaleTranslation = requiredLocaleTranslation.transform(val =>
  sanitize(val, { allowHtml: true, emptyStringToNull: true }));
export type RequiredLocaleTranslation = z.infer<typeof requiredLocaleTranslation>;

export function requiredLocaleTranslationWithLimit(options: { min?: number; max: number }) {
  const { min = 1, max } = options;
  return z.intersection(
    z.object({ en: z.string().min(min).max(max) }),
    z.record(z.string(), z.string().min(min).max(max).nullable()),
  );
}

export function listOption<T extends z.ZodTypeAny = z.ZodString>(valueSchema?: T) {
  return z.object({
    id: z.number().optional(),
    label: z.string().min(1).max(256),
    shortLabel: z.string().min(1).max(256).optional(),
    value: valueSchema ?? z.string().min(1).max(256),
    exclusive: z.boolean().optional(),
    selected: z.boolean().optional(),
    action: actionItem.optional(),
  });
}

export type ListOption<T extends z.ZodTypeAny = z.ZodString> = z.infer<
  ReturnType<typeof listOption<T>>
>;

export function localeOptionList<T extends z.ZodTypeAny = z.ZodString>({ valueSchema, limit }: { valueSchema?: T; limit?: number } = {}) {
  const scheme = z.intersection(
    z.object({
      en: z.array(listOption(valueSchema)),
    }),
    z.record(z.string(), z.array(listOption(valueSchema))),
  );

  if (!limit)
    return scheme;

  return scheme.superRefine((data, ctx) => {
    // @ts-expect-error -- zod4 issue, worked in zod3
    const length = Object.values(data).flat().map(item => item.value).join('').length;
    if (length <= limit)
      return;

    ctx.addIssue({
      code: 'custom',
      message: `Combined options values are too long, limit is ${limit} characters`,
    });
  });
}

export type LocaleOptionList<T extends z.ZodTypeAny = z.ZodString> = z.infer<
  ReturnType<typeof localeOptionList<T>>
>;

export function categoryLocaleOptionList<T extends z.ZodTypeAny = z.ZodNumber>(valueSchema: T) {
  return z.intersection(
    z.object({
      _default: localeOptionList({ valueSchema }),
    }),
    z.record(z.string(), localeOptionList({ valueSchema })),
  );
}

export type CategoryLocaleOptionList<T extends z.ZodTypeAny = z.ZodNumber> = z.infer<
  ReturnType<typeof categoryLocaleOptionList<T>>
>;

export const textDirections = ['ltr', 'rtl'] as const;
export type TextDirection = (typeof textDirections)[number];

export const bigIntString = z.union([z.coerce.bigint().positive(), z.coerce.number().int().positive()]).pipe(z.coerce.string());
export const safeIdentifier = z.string().min(1).regex(/^[\w-]*$/);

const literalSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
type Literal = z.infer<typeof literalSchema>;
type Json = Literal | { [key: string]: Json } | Json[];

export const jsonSchema: z.ZodType<Json> = z.lazy(() =>
  z.union([literalSchema, z.array(jsonSchema), z.record(z.string(), jsonSchema)]),
);

export const jsonObjectSchema: z.ZodType<Json> = z.lazy(() => z.record(z.string(), jsonSchema));
